import AdobeIMS from './IMS'
import { useAuth } from '@hooks/useAuth'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useEffect, useMemo } from 'react'
import { AuthenticationState } from '@store/slices/authSlice'
import { SceneToImageStudioPathname } from '@constants/appConstants'
import { useLaunchDarklyState } from '@hooks/useLaunchDarkly'
import { useRouter } from 'next/router'
import { getUserLocalePreference } from '@concerns/i18n/browser'

const isCurrentPathProtected = (protectedPaths: Array<RegExp>): boolean => {
  return protectedPaths.some(p => window.location.pathname.match(p))
}

const shouldBlockPageWhileCheckingLogin = (
  protectedPaths: Array<RegExp>,
  isAuthenticationStateLoaded: AuthenticationState['isAuthenticationStateLoaded']
) => {
  return isCurrentPathProtected(protectedPaths) && !isAuthenticationStateLoaded
}

const shouldCurrentPathRequireLogin = (
  protectedPaths: Array<RegExp>,
  isAuthenticationStateLoaded: AuthenticationState['isAuthenticationStateLoaded'],
  authStatus: AuthenticationState['status']
): boolean => {
  return (
    isCurrentPathProtected(protectedPaths) &&
    isAuthenticationStateLoaded &&
    authStatus === 'UNAUTHENTICATED' &&
    !window.location.href.includes('#old_hash=&from_ims=true') &&
    // Make an exception for the scene to image studio. Didn't want to edit the regex in protectedPaths because then I'd be essentially hardcoding the firefly studio path
    window.location.pathname !== SceneToImageStudioPathname
  )
}

function RequireAuth({ children }: { children: React.ReactElement }) {
  const {
    isAuthenticationStateLoaded,
    status: authStatus,
    promptLogin
  } = useAuth()
  const flags = useFlags()
  const launchDarklyInitialized = useLaunchDarklyState('initialized')
  const { pathname } = useRouter()

  const allowUnauthenticatedCommunityAccess: boolean | undefined =
    flags['base-tf-ui-allow-unauthenticated-community-access']

  const protectedPaths = useMemo(() => {
    const paths = [/\/studio\//, /\/my-projects/, /\/discover/]
    return allowUnauthenticatedCommunityAccess ? [paths[0], paths[1]] : paths
  }, [allowUnauthenticatedCommunityAccess])

  const isLoginRequired: boolean =
    typeof window === 'undefined'
      ? false
      : shouldCurrentPathRequireLogin(
          protectedPaths,
          isAuthenticationStateLoaded,
          authStatus
        )

  const isPageBlockedWhileCheckingLogin: boolean =
    typeof window === 'undefined'
      ? true
      : shouldBlockPageWhileCheckingLogin(
          protectedPaths,
          isAuthenticationStateLoaded
        )

  useEffect(() => {
    if (!launchDarklyInitialized) return
    if (isLoginRequired && typeof window !== 'undefined') {
      promptLogin({
        authenticationMethod: 'REDIRECT'
      })
    }
  }, [pathname, isLoginRequired, launchDarklyInitialized])

  return isLoginRequired || isPageBlockedWhileCheckingLogin ? null : (
    <>{children}</>
  )
}

export default RequireAuth
